import { reactive } from 'vue'
import { invalidateReadModels } from './readModelCache'
import type { AccountSummary, LoginResponse, UxBootstrap } from './types'

export type SessionEndReason = 'expired' | null

interface SessionState {
  token: string | null
  expiresAt: string | null
  account: AccountSummary | null
  bootstrap: UxBootstrap | null
  rememberMe: boolean
  endReason: SessionEndReason
}

type AuthChannelMessage =
  | {
      type: 'session-request'
      requestId: string
      requesterTabId: string
    }
  | {
      type: 'session-share'
      requestId: string
      targetTabId: string
      token: string
      expiresAt: string | null
      rememberMe: boolean
    }
  | {
      type: 'session-replaced'
      sourceTabId: string
      token: string
      expiresAt: string
      rememberMe: boolean
    }
  | {
      type: 'session-cleared'
      sourceTabId: string
      token: string | null
      reason: SessionEndReason
    }

const AUTH_CHANNEL_NAME = 'craft-imperium-auth-v1'
const TOKEN_KEY = 'ci.token'
const EXPIRES_KEY = 'ci.expires-at'
const REMEMBER_KEY = 'ci.remember-me'
const ACTIVITY_KEY = 'ci.auth.last-server-activity'
const PERSISTENT_SESSION_KEY = 'ci.remember-session-available'
const PERSISTENT_REFRESH_KEY = 'ci.remember-session-last-refresh'
const ACTIVITY_WRITE_INTERVAL_MS = 30_000
const tabId = crypto.randomUUID()
const pendingSessionRequests = new Map<string, (restored: boolean) => void>()
let lastActivityWriteAt = 0

const token = sessionStorage.getItem(TOKEN_KEY)
const expiresAt = sessionStorage.getItem(EXPIRES_KEY)
const rememberMe = sessionStorage.getItem(REMEMBER_KEY) === '1'

export const session = reactive<SessionState>({
  token,
  expiresAt,
  account: null,
  bootstrap: null,
  rememberMe,
  endReason: null,
})

const authChannel =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel(AUTH_CHANNEL_NAME)
    : null

function postAuthMessage(message: AuthChannelMessage): void {
  authChannel?.postMessage(message)
}

function persistSessionToken(
  nextToken: string,
  nextExpiresAt: string | null,
  nextRememberMe: boolean,
): void {
  sessionStorage.setItem(TOKEN_KEY, nextToken)
  if (nextExpiresAt === null) sessionStorage.removeItem(EXPIRES_KEY)
  else sessionStorage.setItem(EXPIRES_KEY, nextExpiresAt)
  sessionStorage.setItem(REMEMBER_KEY, nextRememberMe ? '1' : '0')
}

function applySessionToken(
  nextToken: string,
  nextExpiresAt: string | null,
  nextRememberMe: boolean,
): void {
  const tokenChanged = session.token !== nextToken
  if (tokenChanged) invalidateReadModels()
  session.token = nextToken
  session.expiresAt = nextExpiresAt
  session.rememberMe = nextRememberMe
  session.endReason = null
  if (tokenChanged) {
    session.account = null
    session.bootstrap = null
  }
  persistSessionToken(nextToken, nextExpiresAt, nextRememberMe)
  if (nextRememberMe) localStorage.setItem(PERSISTENT_SESSION_KEY, '1')
  else {
    localStorage.removeItem(PERSISTENT_SESSION_KEY)
    localStorage.removeItem(PERSISTENT_REFRESH_KEY)
  }
}

function clearSessionLocal(reason: SessionEndReason): void {
  invalidateReadModels()
  session.token = null
  session.expiresAt = null
  session.account = null
  session.bootstrap = null
  session.rememberMe = false
  session.endReason = reason
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(EXPIRES_KEY)
  sessionStorage.removeItem(REMEMBER_KEY)
  localStorage.removeItem(PERSISTENT_SESSION_KEY)
  localStorage.removeItem(PERSISTENT_REFRESH_KEY)
}

function isAuthChannelMessage(value: unknown): value is AuthChannelMessage {
  return typeof value === 'object' && value !== null && 'type' in value
}

if (authChannel !== null) {
  authChannel.addEventListener('message', (event: MessageEvent<unknown>) => {
    if (!isAuthChannelMessage(event.data)) return
    const message = event.data

    if (message.type === 'session-request') {
      if (!session.token) return
      postAuthMessage({
        type: 'session-share',
        requestId: message.requestId,
        targetTabId: message.requesterTabId,
        token: session.token,
        expiresAt: session.expiresAt,
        rememberMe: session.rememberMe,
      })
      return
    }

    if (message.type === 'session-share') {
      if (message.targetTabId !== tabId) return
      if (!session.token)
        applySessionToken(message.token, message.expiresAt, message.rememberMe)
      const resolve = pendingSessionRequests.get(message.requestId)
      if (resolve !== undefined) {
        pendingSessionRequests.delete(message.requestId)
        resolve(session.token !== null)
      }
      return
    }

    if (message.type === 'session-replaced') {
      if (message.sourceTabId === tabId) return
      applySessionToken(message.token, message.expiresAt, message.rememberMe)
      return
    }

    if (message.sourceTabId === tabId) return
    if (message.token !== null && session.token !== message.token) return
    clearSessionLocal(message.reason)
  })
}

export function setSession(value: LoginResponse, remember = false): void {
  applySessionToken(value.accessToken, value.expiresAt, remember)
  session.account = value.account
  recordAuthenticatedActivity(value.accessToken, true)
  if (remember) recordPersistentSessionRefresh()
  postAuthMessage({
    type: 'session-replaced',
    sourceTabId: tabId,
    token: value.accessToken,
    expiresAt: value.expiresAt,
    rememberMe: remember,
  })
}

export function setBootstrap(value: UxBootstrap): void {
  session.bootstrap = value
  session.account = value.account
}

export function clearSession(reason: SessionEndReason = null): void {
  const tokenToClear = session.token
  if (tokenToClear === null) {
    if (reason !== null) session.endReason = reason
    return
  }
  clearSessionLocal(reason)
  postAuthMessage({
    type: 'session-cleared',
    sourceTabId: tabId,
    token: tokenToClear,
    reason,
  })
}

export function invalidateSessionToken(tokenToInvalidate: string): boolean {
  if (session.token !== tokenToInvalidate) return false
  clearSession('expired')
  return true
}

export function updateSessionExpiry(
  tokenUsed: string,
  expiresAtValue: string,
): void {
  if (session.token !== tokenUsed) return
  session.expiresAt = expiresAtValue
  sessionStorage.setItem(EXPIRES_KEY, expiresAtValue)
}

export function recordAuthenticatedActivity(
  tokenUsed: string,
  force = false,
): void {
  if (session.token !== tokenUsed) return
  const now = Date.now()
  if (!force && now - lastActivityWriteAt < ACTIVITY_WRITE_INTERVAL_MS) return
  lastActivityWriteAt = now
  localStorage.setItem(ACTIVITY_KEY, String(now))
}

export function hasPersistentSessionHint(): boolean {
  return localStorage.getItem(PERSISTENT_SESSION_KEY) === '1'
}

export function forgetPersistentSessionHint(): void {
  localStorage.removeItem(PERSISTENT_SESSION_KEY)
  localStorage.removeItem(PERSISTENT_REFRESH_KEY)
}

export function recordPersistentSessionRefresh(): void {
  localStorage.setItem(PERSISTENT_REFRESH_KEY, String(Date.now()))
}

export function lastPersistentSessionRefreshAt(): number {
  const stored = Number(localStorage.getItem(PERSISTENT_REFRESH_KEY) ?? '0')
  return Number.isFinite(stored) && stored > 0 ? stored : 0
}

export function lastAuthenticatedActivityAt(): number {
  const stored = Number(localStorage.getItem(ACTIVITY_KEY) ?? '0')
  return Number.isFinite(stored) && stored > 0 ? stored : 0
}

export async function restoreSessionFromPeer(
  timeoutMs = 350,
): Promise<boolean> {
  if (session.token) return true
  if (authChannel === null) return false

  const requestId = crypto.randomUUID()
  return new Promise<boolean>((resolve) => {
    pendingSessionRequests.set(requestId, resolve)
    window.setTimeout(() => {
      const pending = pendingSessionRequests.get(requestId)
      if (pending === undefined) return
      pendingSessionRequests.delete(requestId)
      pending(false)
    }, timeoutMs)
    postAuthMessage({
      type: 'session-request',
      requestId,
      requesterTabId: tabId,
    })
  })
}

const privilegedProfilePermissions = new Set([
  'profile.create',
  'profile.edit',
  'profile.import',
  'profile.review',
  'profile.publish',
])

const contentAdminPermissions = new Set([
  'content.read',
  'content.create',
  'content.publish',
])

const operationsAdminPermissions = new Set([
  'admin.health.read',
  'admin.maintenance.manage',
  'admin.announcement.publish',
  'admin.export',
  'admin.import',
])

export function adminEntryPath(): string | null {
  const permissions = session.bootstrap?.permissions ?? []
  const has = (permission: string): boolean => permissions.includes(permission)
  const hasPrefix = (prefix: string): boolean =>
    permissions.some((permission) => permission.startsWith(prefix))
  const hasAny = (candidates: Set<string>): boolean =>
    permissions.some((permission) => candidates.has(permission))

  if (has('admin.dashboard.read')) return '/admin'
  if (hasAny(privilegedProfilePermissions)) return '/admin/design'
  if (hasAny(contentAdminPermissions)) return '/admin/content'
  if (hasPrefix('balance.')) return '/admin/balance'
  if (hasPrefix('gm.')) return '/admin/gm'
  if (hasPrefix('trust_safety.')) return '/admin/trust-safety'
  if (hasPrefix('asset.') || hasAny(operationsAdminPermissions))
    return '/admin/operations'
  if (has('admin.search')) return '/admin/accounts'
  if (has('admin.audit.read')) return '/admin/audit'
  if (permissions.some((permission) => permission.startsWith('admin.')))
    return '/admin'

  return null
}

export function hasAdminPermission(): boolean {
  return adminEntryPath() !== null
}
